import { Sine, TimelineLite, TimelineMax } from "gsap";
import * as HLF from "html-living-framework";
import { Emitter } from "pixi-particles";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import { PowerupData } from "../Data/PowerupData";
import * as SFX from "../Data/SoundEffectKeys";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";
import { WorldElement } from "../GameObjects/WorldElement";
import { BoxCollider } from "../Physics/BoxCollider";

export class Powerup extends WorldElement {
    private static readonly SCALE_NORM: number = 0.65;

    private _visuals: PIXI.Container;
    private _base: PIXI.Sprite;
    private _shine: PIXI.Sprite;
    private _icon: PIXI.Sprite;
    private _confetti: Emitter;
    private _confettiContainer: PIXI.Container;

    private _hoverTween: TimelineMax | undefined;
    private _collectedTween: TimelineLite | undefined;

    constructor() {
        super();
        this._active = true;

        this._visuals = new PIXI.Container();
        this.addChild(this._visuals);

        this._base = HLF.PixiUtils.makeSprite("powerup-base", Constants.SPRITESHEET_GAMEPLAY);
        this._base.anchor.set(0, 0.5);
        this._visuals.addChild(this._base);

        this._icon = new PIXI.Sprite();
        this._icon.anchor.set(0, 0.5);
        this._visuals.addChild(this._icon);

        this._shine = HLF.PixiUtils.makeSprite("powerup-shine", Constants.SPRITESHEET_GAMEPLAY);
        this._shine.anchor.set(0, 0.5);
        this._visuals.addChild(this._shine);

        const shapes: string[] = [ "rect", "circle", "tri" ];
        const particles: any[] = new Array<any>();
        const sheet: PIXI.loaders.Resource = PIXI.loader.resources[Constants.SPRITESHEET_UI];
        for (let i = 0; i < 4; ++i) {
            for (let j = 0; j < shapes.length; ++j) {
                particles.push(sheet.textures!["particle-confetti-" + shapes[j] + "-0" + i]);
            }
        }

        this._confettiContainer = new PIXI.Container();
        this._confettiContainer.position.set(this._base.width * 0.5 * Powerup.SCALE_NORM, 0);
        this._confettiContainer.scale.set(0.7);
        this.addChild(this._confettiContainer);
        this._confetti = new Emitter(
            this._confettiContainer,
            particles,
            {
                alpha: {
                    list: [
                        { value: 0.1, time: 0 },
                        { value: 0.8, time: 0.1 },
                        { value: 0.8, time: 0.8 },
                        { value: 0.1, time: 1 },
                    ],
                    isStepped: false,
                },
                speed: {
                    list: [
                        { value: 300, time: 0 },
                    ],
                    isStepped: false,
                    minimumSpeedMultiplier: 0.1,
                },
                // scale: {
                //     startScale: 0.5,
                //     minimumScaleMultiplier: 0.5,
                // },
                acceleration: new PIXI.Point(0, 500),
                startRotation: { min: 135, max: 405 },
                rotationSpeed: { min: 0, max: 0 },
                lifetime: { min: 0.75, max: 1.0 },
                frequency: 0.01,
                spawnChance: 1,
                particlesPerWave: 30,
                emitterLifetime: 0.75,
                maxParticles: 50,
                pos: { x: 0, y: 0 },
                addAtBack: false,
                spawnType: "circle",
                spawnCircle: { x: 0, y: 0, r: 30 },
            },
        );
        this._confetti.emit = false;

        this._collider = new BoxCollider(10, 90, -40, 40);
        this._visuals.scale.set(Powerup.SCALE_NORM);
    }

    public init(pData: PowerupData): void {
        this._data = pData;

        this._base.tint = pData.tint;
        this._icon.texture = PIXI.loader.resources[Constants.SPRITESHEET_GAMEPLAY].textures![this._data.asset];
        this._visuals.alpha = 1;
        this._visuals.scale.set(Powerup.SCALE_NORM);
        this.visible = true;
        this._active = true;
        this._collisionSfx = HLF.ArrayUtils.random(SFX.POP);

        this.playHoverAnim();
    }

    public update(pDeltaTime: number): void {
        this._confetti.update(pDeltaTime);
    }

    public onCollision(pIsBalloonInvuln: boolean): void {
        super.onCollision(pIsBalloonInvuln);
        this.playCollectedAnim();
        if ((this._data as PowerupData).vo.length > 0) {
            const success: boolean = Application.instance.voiceOver.play(
                HLF.ArrayUtils.random((this._data as PowerupData).vo),
                InteruptType.LESS,
                VoiceCategory.FUN_FACT,
            );
            if (success) {
                (this._data as PowerupData).vo.pop();
            }
        }
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        super.destroy(pOptions);
        if (this._hoverTween !== undefined) { this._hoverTween.kill(); }
        if (this._collectedTween !== undefined) { this._collectedTween.kill(); }
    }

    private playHoverAnim(): void {
        if (this._hoverTween !== undefined) { this._hoverTween.kill(); }
        this._hoverTween = new TimelineMax({
            repeat: -1,
        });

        const x: number[] = [ -8, 8 ];
        const y: number[] = [ -15, 0, -15, 0 ];
        const rotations: number[] = [ -0.05, 0.05 ];
        const dur: number = 5.0;

        this._visuals.position.set(x[x.length - 1], y[y.length - 1]);
        this._visuals.rotation = rotations[rotations.length - 1];

        for (let i = 0; i < x.length; ++i) {
            this._hoverTween.to(this._visuals, dur * 0.5, {
                x: x[i],
                rotation: rotations[i],
                ease: Sine.easeInOut,
            }, (dur * 0.5) * i);
        }
        for (let i = 0; i < y.length; ++i) {
            this._hoverTween.to(this._visuals, dur * 0.25, {
                y: y[i],
                ease: Sine.easeInOut,
            }, (dur * 0.25) * i);
        }
    }

    private playCollectedAnim(): void {
        if (this._hoverTween !== undefined) { this._hoverTween.kill(); }
        if (this._collectedTween !== undefined) { this._collectedTween.kill(); }
        this._collectedTween = new TimelineLite();
        this._collectedTween.to(this._visuals, 0.5, {
            alpha: 0,
            ease: Sine.easeInOut,
        }, 0);
        this._collectedTween.to(this._visuals.scale, 0.5, {
            x: Powerup.SCALE_NORM * 1.25,
            y: Powerup.SCALE_NORM * 1.25,
            ease: Sine.easeInOut,
        }, 0);
        this._confetti.emit = true;
    }
}
